import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import NextLink from '../NextLink';
import { colors, maskTextStyles } from '../../constants/styleConstants';
import { route } from '../../constants/routes';
import { getRouteWithSlug, getWorkRoleCanonicalName, getWorkRoleTranslationName } from '../../utils/common';
import { EMPTY_INFO_MARK, TP } from '../../constants/consts';
import { selectIsLoggedInState } from '../../store/selectors';
import LoginDialog from '../LoginDialog';
import { useTranslation } from '../../src/i18n';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
  },
  profession: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
    width: '50%',
    margin: '4px 7px',
    color: colors.grey3,
  },
  name: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'left',
    width: '50%',
    margin: '4px 7px',
    flexWrap: 'wrap',
  },
  nameItem: {
    lineHeight: 1.2,
  },
  linkItem: {
    lineHeight: 1.2,
    color: colors.blueChip,
    '&:hover': {
      color: colors.blueChip,
    },
  },
  maskText: {
    textShadow: '0 0 5px rgba(4, 145, 255, 0.4)',
    cursor: 'pointer',
    ...maskTextStyles,
    '&:hover': {
      textShadow: '0 0 5px rgba(4, 145, 255, 0.4)',
      color: 'transparent',
    },
  },
  qualification: {
    marginLeft: 5,
    lineHeight: 1.3,
  },
  nameWrapper: {
    lineHeight: 'normal',
    '&::before': {
      content: "'/'",
      padding: '0 3px',
      color: colors.subTitle,
    },
    '&:first-of-type::before': {
      display: 'none',
    },
  },
  roleName: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
    lineHeight: 'normal',
    wordBreak: 'break-all',
    alignItems: 'flex-end',
    flexDirection: 'column',
    fontStyle: props => props.type !== 'crew' && 'italic',
  },
}));

const CastInfo = ({ cast }) => {
  const classes = useStyles();
  if (cast.length <= 0 || !cast[0]?.workRole) {
    return '~';
  }
  const canonicalName = getWorkRoleCanonicalName(cast[0]);
  const translationName = getWorkRoleTranslationName(cast[0]);
  return (
    <div className={classes.roleName}>
      {!isEmpty(canonicalName) && <span style={{ lineHeight: 'normal' }}>{canonicalName}</span>}
      {!isEmpty(translationName) && <span style={{ lineHeight: 'normal' }}>&nbsp;({translationName})</span>}
    </div>
  );
};

const CastRow = props => {
  const { castGroupByRole, className, showText, isLoggedIn, type } = props;
  const classes = useStyles(props);
  const [isLoginDialogOpen, setLoginDialog] = React.useState(false);
  const router = useRouter();
  const { t } = useTranslation();

  const checkArchivalAccess = () => {
    if (isLoggedIn) {
      router.push(route.CASTING_TOOL);
    } else {
      setLoginDialog(true);
    }
  };

  return (
    <>
      {castGroupByRole &&
        Object.keys(castGroupByRole || {}).map((key, keyMember) => {
          const isRenderCast = castGroupByRole[key].some(item => {
            if (item.profile || item.profileReference) {
              return item.profile || item.profileReference;
            }
          });
          return (
            <div key={keyMember}>
              {isRenderCast && (
                <div className={classes.container}>
                  <div className={classnames(classes.profession, className)}>
                    {type !== 'cast' && <span className={classes.roleName}>{key === 'null' ? '~' : key}</span>}
                    {type === 'cast' && <CastInfo cast={castGroupByRole[key]} />}
                  </div>

                  <div className={classes.name}>
                    {castGroupByRole[key]?.length > 0
                      ? castGroupByRole[key].map((item, member) => {
                          const theProfile = get(item, 'profile');
                          const linkProps = {
                            href: `${route.ARTISTS}/[id]/[[...action]]`,
                            as: getRouteWithSlug(route.ARTISTS, get(item, 'profile')),
                            target: '_blank',
                          };
                          return (
                            <span key={member} className={classes.nameWrapper}>
                              {linkProps &&
                              theProfile &&
                              !theProfile?.is_hidden &&
                              (item.profile || item.profileReference) &&
                              !showText &&
                              item.id !== -1 ? (
                                <NextLink
                                  {...linkProps}
                                  className={classnames(classes.linkItem, { [classes.maskText]: item.id === -1 })}
                                >
                                  <span className={classes.nameItem}>
                                    {item.profile ? item.profile.name : item.profileReference}
                                  </span>
                                </NextLink>
                              ) : (
                                <>
                                  {(item.profile ||
                                    item.profileReference ||
                                    item.id === -1 ||
                                    theProfile?.is_hidden) && (
                                    <span
                                      className={classnames(classes.nameItem, { [classes.maskText]: item.id === -1 })}
                                      onClick={() => item.id === -1 && checkArchivalAccess()}
                                    >
                                      {item.profile ? item.profile.name : item.profileReference}
                                    </span>
                                  )}
                                </>
                              )}
                            </span>
                          );
                        })
                      : EMPTY_INFO_MARK}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      <LoginDialog
        isOpen={isLoginDialogOpen}
        url={{ as: router.asPath }}
        onClose={() => setLoginDialog(false)}
        message={t(`${TP}.ARCHIVAL_ACCESS_MESSAGE`)}
      />
    </>
  );
};

CastRow.propTypes = {
  castGroupByRole: PropTypes.object,
  showText: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
};

CastRow.defaultProps = {
  short: false,
  showText: false,
};

const mapStateToProps = createStructuredSelector({
  isLoggedIn: selectIsLoggedInState(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(CastRow);
